import { BarChart, BarChartProps } from '@mui/x-charts/BarChart';
import { PaymentMethodComparison, formatMoney } from '@dill/dill-shared';
import { COLORS } from '../../../../../utils/colors';

interface PaymentMethodComparisonChartProps {
  data: PaymentMethodComparison[];
}

const getResponsiveConfig = (): Partial<BarChartProps> => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  return {
    height: isMobile ? 300 : 350,
    margin: {
      left: isMobile ? 60 : 80,
      right: isMobile ? 15 : 20,
      top: 20,
      bottom: isMobile ? 60 : 60,
    },
    hideLegend: false,
  };
};

function PaymentMethodComparisonChart({ data }: PaymentMethodComparisonChartProps) {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-gray-400">
        No payment method data available
      </div>
    );
  }

  const dataSet = data.map((method) => ({
    method: method.method,
    averageSize: method.averageSize,
    transactionCount: method.transactionCount,
    totalVolume: method.totalVolume,
  }));

  return (
    <div className="w-full">
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 mb-6">
        <div>
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-1">
            Payment Method Comparison
          </h3>
          <p className="text-sm text-gray-500">Average transaction size by payment method</p>
        </div>
      </div>
      <BarChart
        borderRadius={8}
        dataset={dataSet}
        xAxis={[
          {
            dataKey: 'method',
            tickLabelStyle: {
              fontSize: 12,
              fill: COLORS.TEXT_SECONDARY,
              fontFamily: 'Inter',
              fontWeight: 'bold',
            },
          },
        ]}
        series={[
          {
            dataKey: 'averageSize',
            label: 'Average Transaction Size',
            color: COLORS.INFO_500,
            valueFormatter: (value: number | null) => {
              return `${formatMoney(value || 0, 2)}`;
            },
          },
        ]}
        yAxis={[
          {
            width: 80,
            tickLabelStyle: {
              fontSize: 11,
              fill: COLORS.TEXT_SECONDARY,
              fontFamily: 'Inter',
              fontWeight: 'bold',
            },
            domainLimit: (min, max) => {
              return { min, max: Math.floor(max + (max * 10) / 100) };
            },
            valueFormatter: (value: number) => {
              return formatMoney(value, 0);
            },
          },
        ]}
        grid={{ horizontal: true }}
        {...getResponsiveConfig()}
      />

      {/* Summary Stats */}
      <div className="grid grid-cols-2 gap-4 mt-6">
        {data.map((method) => {
          const isCard = method.method === 'CARD';
          const colors = isCard
            ? { bg: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-200' }
            : { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200' };
          return (
            <div
              key={method.method}
              className={`p-4 rounded-lg ${colors.bg} border ${colors.border}`}>
              <p className={`text-sm font-medium ${colors.text}`}>{method.method}</p>
              <div className="mt-2 space-y-1">
                <div className="flex justify-between">
                  <span className="text-xs text-gray-600">Avg Size</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {formatMoney(method.averageSize, 0)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-xs text-gray-600">Transactions</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {method.transactionCount.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-xs text-gray-600">Total Volume</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {formatMoney(method.totalVolume, 0)}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default PaymentMethodComparisonChart;
